"use client";

import { useTheaterOverlayFrozen } from "@/components/feature/theater-overlay-freeze";
import { useEffect, useMemo, useRef, useState } from "react";

type WordSpec = {
  text: string;
  color: string; // e.g. "#0FFA68"
};

type AnimatedWordCyclerProps = {
  words: WordSpec[];
  className?: string;
  transitionMs?: number; // duration for letters to move in/out
  holdMs?: number; // time to hold a word before switching
  staggerMs?: number; // delay between each letter
  outDistancePct?: number; // how far letters move up when exiting
  outBlurPx?: number; // how much blur to apply while exiting
  nextEnterDelayMs?: number; // delay before next word starts entering (to reduce overlap)
  enterOvershootPct?: number; // positive percent overshoot when letters enter
};

function CurrentLayer({
  word,
  currentIndex,
  isTransitioning,
  mounted,
  transitionMs,
  staggerMs,
  outDistancePct,
  outBlurPx,
}: {
  word: WordSpec;
  currentIndex: number;
  isTransitioning: boolean;
  mounted: boolean;
  transitionMs: number;
  staggerMs: number;
  outDistancePct: number;
  outBlurPx: number;
}) {
  const letterStyle = (i: number, ease: "in" | "out") => ({
    transitionProperty: "transform, opacity, filter",
    transitionDuration: `${transitionMs}ms`,
    transitionDelay: `${i * staggerMs}ms`,
    transitionTimingFunction:
      ease === "in" ? "cubic-bezier(0, 0, 0.2, 1)" : "cubic-bezier(0.4, 0, 1, 1)",
  });

  const getTransform = () => {
    if (!mounted) return "translateY(100%)";
    if (isTransitioning) return `translateY(-${outDistancePct}%)`;
    return "translateY(0%)";
  };

  const getOpacity = () => {
    if (!mounted || isTransitioning) return 0;
    return 1;
  };

  const getFilter = () => {
    if (!mounted) return "blur(0px)";
    if (isTransitioning) return `blur(${outBlurPx}px)`;
    return "blur(0px)";
  };

  return (
    <span className="absolute inset-0 block whitespace-nowrap" aria-live="off">
      {word.text.split("").map((ch, i) => {
        const styleBase = letterStyle(i, isTransitioning ? "out" : "in");
        const transform = getTransform();
        const opacity = getOpacity();
        const filter = getFilter();
        return (
          <span
            // eslint-disable-next-line react/no-array-index-key
            key={`curr-${currentIndex}-${i}-${ch}`}
            className="inline-block will-change-transform"
            style={{ color: word.color, ...styleBase, transform, opacity, filter }}
          >
            {ch}
          </span>
        );
      })}
    </span>
  );
}

function NextLayer({
  word,
  nextIndex,
  transitionMs,
  staggerMs,
  nextAnimating,
}: {
  word: WordSpec;
  nextIndex: number;
  transitionMs: number;
  staggerMs: number;
  nextAnimating: boolean;
}) {
  const letterStyle = (i: number) => ({
    transitionProperty: "transform, opacity, filter",
    transitionDuration: `${transitionMs}ms`,
    transitionDelay: `${i * staggerMs}ms`,
    transitionTimingFunction: "cubic-bezier(0, 0, 0.2, 1)",
  });
  const enterAnimationStyle = (i: number) => ({
    animationName: "pm-letter-enter",
    animationDuration: `${transitionMs}ms`,
    animationDelay: `${i * staggerMs}ms`,
    animationTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)",
    animationFillMode: "both" as const,
  });

  return (
    <span className="absolute inset-0 block whitespace-nowrap" aria-live="off">
      {word.text.split("").map((ch, i) => {
        const styleBase = nextAnimating ? undefined : letterStyle(i);
        const transform = nextAnimating ? "translateY(0%)" : "translateY(100%)";
        const opacity = nextAnimating ? 1 : 0;
        const filter = nextAnimating ? "blur(0px)" : "blur(0px)";
        const anim = nextAnimating ? enterAnimationStyle(i) : undefined;
        return (
          <span
            // eslint-disable-next-line react/no-array-index-key
            key={`next-${nextIndex}-${i}-${ch}`}
            className="inline-block will-change-transform"
            style={{
              color: word.color,
              ...(styleBase ?? {}),
              ...(anim ?? {}),
              transform,
              opacity,
              filter,
            }}
          >
            {ch}
          </span>
        );
      })}
    </span>
  );
}

export function AnimatedWordCycler({
  words,
  className,
  transitionMs = 450,
  holdMs = 1000,
  staggerMs = 40,
  outDistancePct = 60,
  outBlurPx = 10,
  nextEnterDelayMs = 220,
  enterOvershootPct = 3,
}: AnimatedWordCyclerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [nextAnimating, setNextAnimating] = useState(false);
  const timeoutsRef = useRef<number[]>([]);
  const overlayFrozen = useTheaterOverlayFrozen();

  const longestWord = useMemo(() => {
    return words.reduce((a, b) => (a.text.length >= b.text.length ? a : b));
  }, [words]);

  const currentWord = words[currentIndex];
  const nextIndex = (currentIndex + 1) % words.length;
  const nextWord = words[nextIndex];

  // Kick off initial enter animation
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Schedule transitions after initial enter
  useEffect(() => {
    for (const t of timeoutsRef.current) {
      clearTimeout(t);
    }
    timeoutsRef.current = [];

    if (overlayFrozen) return;

    // Compute total time for intro enter to fully complete (with last letter stagger)
    const introTotal = transitionMs + (currentWord.text.length - 1) * staggerMs;

    // After intro enter + hold, start crossfade transition
    const t1 = window.setTimeout(() => {
      // Begin cross transition
      setIsTransitioning(true);

      // Start the next word after a slight delay to reduce overlap
      const tStartNext = window.setTimeout(() => {
        setNextAnimating(true);
      }, nextEnterDelayMs);
      timeoutsRef.current.push(tStartNext);

      // End cross transition after letters finish (use max letters between current and next)
      const maxLetters = Math.max(currentWord.text.length, nextWord.text.length);
      const crossTotal = nextEnterDelayMs + transitionMs + (maxLetters - 1) * staggerMs;
      const t2 = window.setTimeout(() => {
        setIsTransitioning(false);
        setNextAnimating(false);
        setCurrentIndex(nextIndex);
      }, crossTotal);
      timeoutsRef.current.push(t2);
    }, introTotal + holdMs);

    timeoutsRef.current.push(t1);
    return () => {
      for (const t of timeoutsRef.current) {
        clearTimeout(t);
      }
      timeoutsRef.current = [];
    };
    // We intentionally exclude nextWord/nextIndex here to avoid restarting timers mid-transition
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, currentIndex, overlayFrozen, transitionMs, holdMs, staggerMs]);

  return (
    <span className={`relative inline-block leading-none ${className ?? ""}`}>
      {/* Layout placeholder so container keeps height */}
      <span className="invisible select-none" aria-hidden>
        {longestWord.text}
      </span>

      {/* Current layer */}
      <CurrentLayer
        word={currentWord}
        currentIndex={currentIndex}
        isTransitioning={isTransitioning}
        mounted={mounted}
        transitionMs={transitionMs}
        staggerMs={staggerMs}
        outDistancePct={outDistancePct}
        outBlurPx={outBlurPx}
      />

      {/* Next layer (only mounted during transition) */}
      {isTransitioning && (
        <NextLayer
          word={nextWord}
          nextIndex={nextIndex}
          transitionMs={transitionMs}
          staggerMs={staggerMs}
          nextAnimating={nextAnimating}
        />
      )}
      <style jsx>{`
        @keyframes pm-letter-enter {
          0% { transform: translateY(100%); opacity: 0; }
          65% { transform: translateY(-${enterOvershootPct}%); opacity: 1; }
          100% { transform: translateY(0%); opacity: 1; }
        }
      `}</style>
    </span>
  );
}
